import React, { useState, useEffect, useRef, memo } from 'react';
import { usePhotoCache } from '../contexts/PhotoCacheContext';

/**
 * Shared PlacePhoto component with:
 * - Global photo caching (deduplication across all pages)
 * - Lazy loading via Intersection Observer
 * - Memoization to prevent unnecessary re-renders
 */
const PlacePhoto = memo(function PlacePhoto({
  placeId,
  placeName,
  storedPhotoUrl = null,
  photoUrlSavedAt = null,
  className = '',
  loadingClassName = 'loading',
  errorClassName = '',
  placeholderContent = null,
  loadingContent = null,
  imgProps = {},
}) {
  const { getPhotoUrl, refreshPhotoUrl } = usePhotoCache();
  const [photoUrl, setPhotoUrl] = useState(null);
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useRef(null);
  const fetchAttemptedRef = useRef(false);
  const refreshAttemptedRef = useRef(false);

  // Intersection Observer for lazy loading
  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            observer.unobserve(element);
          }
        });
      },
      {
        rootMargin: '100px', // Start loading 100px before visible
        threshold: 0,
      }
    );

    observer.observe(element);

    return () => {
      observer.unobserve(element);
    };
  }, []);

  // Reset state when placeId changes
  useEffect(() => {
    setPhotoUrl(null);
    setHasError(false);
    setIsLoading(true);
    fetchAttemptedRef.current = false;
    refreshAttemptedRef.current = false;
  }, [placeId]);

  // Fetch photo when visible and placeId is available
  useEffect(() => {
    if (!isVisible || fetchAttemptedRef.current) return;

    const fetchPhoto = async () => {
      fetchAttemptedRef.current = true;

      if (!placeId) {
        setHasError(true);
        setIsLoading(false);
        return;
      }

      try {
        // Pass stored photo data to avoid redundant API calls
        const url = await getPhotoUrl(placeId, {
          storedPhotoUrl,
          photoUrlSavedAt,
        });
        setPhotoUrl(url);
        setHasError(false);
      } catch (err) {
        setHasError(true);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPhoto();
  }, [isVisible, placeId, getPhotoUrl, storedPhotoUrl, photoUrlSavedAt]);

  // Default placeholder SVG
  const defaultPlaceholder = (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
      <circle cx="12" cy="13" r="4"/>
      <line x1="1" y1="1" x2="23" y2="23"/>
    </svg>
  );

  // Default loading SVG
  const defaultLoading = (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
      <circle cx="12" cy="13" r="4"/>
    </svg>
  );

  // Show placeholder if error or no way to get photo
  if (hasError || (!placeId && !isLoading)) {
    return (
      <div ref={containerRef} className={`${className} ${errorClassName}`.trim()}>
        {placeholderContent || defaultPlaceholder}
      </div>
    );
  }

  // Show loading placeholder while fetching or not yet visible
  if (!isVisible || isLoading || !photoUrl) {
    return (
      <div ref={containerRef} className={`${className} ${loadingClassName}`.trim()}>
        {loadingContent || defaultLoading}
      </div>
    );
  }

  // Handle image load error - try refreshing once before giving up
  const handleImageError = async () => {
    // If we haven't tried refreshing yet, attempt to get a fresh URL
    if (!refreshAttemptedRef.current && placeId) {
      refreshAttemptedRef.current = true;
      setIsLoading(true);
      try {
        const freshUrl = await refreshPhotoUrl(placeId);
        setPhotoUrl(freshUrl);
        setIsLoading(false);
      } catch (err) {
        // Refresh failed, show error state
        setHasError(true);
        setIsLoading(false);
      }
    } else {
      // Already tried refreshing, give up
      setHasError(true);
    }
  };

  return (
    <img
      ref={containerRef}
      src={photoUrl}
      alt={placeName || 'Place photo'}
      className={className}
      onError={handleImageError}
      {...imgProps}
    />
  );
});

export default PlacePhoto;
